import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { PageHero } from '../../components/ui/PageHero';
import { Section } from '../../components/ui/Section';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { MockupTag } from '../../components/ui/MockupTag';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { galleryItems } from '../../data/pageContent';
import { images } from '../../data/images';
const filters = ['All', 'Classrooms', 'Student life', 'Accommodation', 'Locations'];
export function Media() {
  usePageMeta('ES World Media | Photos of Classes and Student Life', 'See photos of our classrooms, student life, rooms and campuses. Picture your English course before you book.');
  const [filter, setFilter] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const visible = galleryItems.filter(item => filter === 'All' || item.category === filter);
  const openItem = openIndex !== null ? visible[openIndex] : null;
  useEffect(() => {
    if (openIndex === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenIndex(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex]);
  return <>
      <PageHero breadcrumbs={[{
      label: 'About Us',
      to: '/about/who-we-are'
    }, {
      label: 'Media',
      to: '/about/media'
    }]} title="ES World media: see student life" lead="Look at photos from our classrooms, activities and student homes. Our media page helps you picture your first week, from your first lesson to your first weekend trip." primary={{
      label: 'Plan your first week',
      to: '#enquire'
    }} secondary={{
      label: 'Browse all photos',
      to: '#photos'
    }} image={images.social} imageAlt="Students taking photos on a city tour" data-id="element-963" data-mp="kfk17f3n65n7" />

      <Section id="photos" title="Picture yourself here" description="Choose a topic to see more photos. Select a photo to see it larger." mockup="Placeholder photos" aside={<TextLink to="/destinations" data-id="element-965" data-mp="myfaccgsfyos">Compare our locations</TextLink>} data-id="element-964" data-mp="wsechbgv3igq">
        <div role="tablist" aria-label="Photo topics" className="mb-8 flex gap-2 overflow-x-auto pb-2" data-id="element-966" data-mp="j0hmpe2o1kni">
          {filters.map(item => <button key={item} type="button" role="tab" aria-selected={filter === item} onClick={() => {
          setFilter(item);
          setOpenIndex(null);
        }} className={`h-11 shrink-0 whitespace-nowrap rounded-full border-2 bg-white px-5 type-button font-bold transition-colors duration-150 ${filter === item ? 'border-brand text-ink' : 'border-line text-body hover:border-muted hover:text-ink'}`} data-id="element-967" data-mp="90h0nkdewqfm">
              {item}
            </button>)}
        </div>
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5" data-id="element-968" data-mp="2fopt395kuuda">
          {visible.map((item, index) => <li key={item.src + item.category} data-id="element-969" data-mp="2kmrbdrv6ipnp" data-mpi={index}>
              <button type="button" onClick={() => setOpenIndex(index)} className="group block w-full overflow-hidden rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" aria-label={`Open photo: ${item.alt}`} data-id="element-970" data-mp="1nxtfu7flunph">
                <img src={item.src} alt={item.alt} className="aspect-square w-full object-cover transition-transform duration-300 ease-brand group-hover:scale-[1.03]" data-id="element-971" data-mp="21xupab9xr3gk" />
              </button>
              <p className="mt-2 type-small text-muted" data-id="element-972" data-mp="3dc88h2tquuq9">{item.category}</p>
            </li>)}
        </ul>
        <div className="mt-12" data-id="element-973" data-mp="q5bvcg82iabj">
          <ActionLink to="/programmes" data-id="element-974" data-mp="q4k9hx49vqyg">Find your programme</ActionLink>
        </div>
      </Section>

      <AnimatePresence data-id="element-975" data-mp="l71xfxuykvm8">
        {openItem ? <motion.div role="dialog" aria-modal="true" aria-label={openItem.alt} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-5" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setOpenIndex(null)} data-id="element-976" data-mp="z5i63ucdsw70">
            <motion.figure initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative max-h-full max-w-4xl" onClick={event => event.stopPropagation()} data-id="element-977" data-mp="1huf160flrsqa">
              <img src={openItem.src} alt={openItem.alt} className="max-h-[80vh] w-auto rounded-2xl object-contain" data-id="element-978" data-mp="26ftjbx9krudz" />
              <figcaption className="mt-3 flex items-center justify-between gap-4 text-white" data-id="element-979" data-mp="2beekbywnii3h">
                <span data-id="element-980" data-mp="1wh5btsf7uxoj">{openItem.alt}</span>
                <MockupTag note="Placeholder" data-id="element-981" data-mp="1wk9m97j9ypnn" />
              </figcaption>
              <button type="button" onClick={() => setOpenIndex(null)} aria-label="Close photo" className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white text-ink transition-colors duration-150 hover:bg-mist" data-id="element-982" data-mp="21foyxw5jqfih">
                <XIcon className="h-5 w-5" data-id="element-983" data-mp="1dpumhfp52syr" />
              </button>
            </motion.figure>
          </motion.div> : null}
      </AnimatePresence>

      <EnquirySection title="Start your own story" description="Tell us which programme and location you like. Our team helps you plan your first week." submitLabel="Send my enquiry today" data-id="element-984" data-mp="14s2aakeps1fl" />
    </>;
}